import type { NavItem, PageLevelHeading } from "@gadgetinc/widgets/src/types/navigation-types";
import { GadgetOnlyHeader } from "@gadgetinc/widgets/src/types/navigation-types";
import { CodeNavTitle } from "docs/src/chrome/nav/CodeNavTitle";
import React from "react";
import { Divider } from "../shared/Divider";

type TemplateNavItem = NavItem & PageLevelHeading;

export const getApiReferencesDocsNavItems = (props: {
  apiReferenceBase: string;
  templateArchitectureNav?: TemplateNavItem[];
  templateSetupNav?: TemplateNavItem[];
  modelNamespacedApiIdentifiers: string[];
  hasGlobalActions?: boolean;
  hasGlobalViews?: boolean;
}) => {
  const {
    constantApiReferencesDocsNav,
    schemaApiDocsNavItems: { models, globals },
  } = getGroupedApiReferencesDocsNavItems(props);
  return [...constantApiReferencesDocsNav, ...models, ...(globals ?? [])];
};

export const getGroupedApiReferencesDocsNavItems = (props: {
  apiReferenceBase: string;
  templateArchitectureNav?: TemplateNavItem[];
  templateSetupNav?: TemplateNavItem[];
  modelNamespacedApiIdentifiers: string[];
  hasGlobalActions?: boolean;
  hasGlobalViews?: boolean;
}) => {
  return {
    constantApiReferencesDocsNav: getConstantApiReferencesDocsNavItems(props),
    schemaApiDocsNavItems: getSchemaApiDocsNavItems(props),
  };
};

const getConstantApiReferencesDocsNavItems = (props: {
  apiReferenceBase: string;
  templateArchitectureNav?: TemplateNavItem[];
  templateSetupNav?: TemplateNavItem[];
}) => {
  const { apiReferenceBase, templateArchitectureNav, templateSetupNav } = props;
  return [
    {
      title: `API Overview`,
      itemId: apiReferenceBase,
      nodeName: "H1" as const,
    },

    ...(templateArchitectureNav ?? []),
    ...(templateSetupNav ?? []),

    {
      title: `API calls within Gadget`,
      itemId: `${apiReferenceBase}/api-calls-within-gadget`,
      nodeName: "H1" as const,
    },
    {
      title: "External API calls with Gadget",
      itemId: `${apiReferenceBase}/external-api-calls`,
      nodeName: "H1" as const,
      subNav: [
        {
          nodeName: "H1" as const,
          title: "Installing",
          itemId: `${apiReferenceBase}/external-api-calls/installing`,
        },
        {
          nodeName: "H1" as const,
          title: "Authentication",
          itemId: `${apiReferenceBase}/external-api-calls/authentication`,
        },
        {
          nodeName: "H1" as const,
          title: "GraphQL",
          itemId: `${apiReferenceBase}/external-api-calls/graphql`,
        },
      ],
    },
    {
      nodeName: "H1" as const,
      title: <CodeNavTitle>Record</CodeNavTitle>,
      itemId: `${apiReferenceBase}/gadget-record`,
    },
    {
      nodeName: "H1" as const,
      title: <CodeNavTitle>BackgroundActionHandle</CodeNavTitle>,
      itemId: `${apiReferenceBase}/background-action-handle`,
    },
    {
      nodeName: "H1" as const,
      title: "Internal vs Public API",
      itemId: `${apiReferenceBase}/internal-api`,
    },
    {
      nodeName: "H1" as const,
      title: "Sorting, filtering, and searching",
      itemId: `${apiReferenceBase}/sorting-and-filtering`,
    },
    {
      nodeName: "H1" as const,
      title: "Errors",
      itemId: `${apiReferenceBase}/errors`,
    },
    {
      title: <Divider />,
      itemId: `${apiReferenceBase}/api-schema/divider`,
      disabled: true,
      onlyHeader: GadgetOnlyHeader.Header,
    },
  ];
};

const getSchemaApiDocsNavItems = (props: {
  apiReferenceBase: string;
  modelNamespacedApiIdentifiers: string[];
  hasGlobalActions?: boolean;
  hasGlobalViews?: boolean;
}) => {
  const { apiReferenceBase, modelNamespacedApiIdentifiers, hasGlobalActions, hasGlobalViews } = props;

  const models = [
    {
      title: "Schema",
      itemId: `${apiReferenceBase}/schema`,
      disabled: true,
      onlyHeader: GadgetOnlyHeader.BoldedHeader,
    },
    ...modelNamespacedApiIdentifiers
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
      .map((modelNamespacedApiIdentifier) => {
        // Use "." as separator in URL to avoid wildcard route patterns
        const modelUrlIdentifier = modelNamespacedApiIdentifier.replaceAll("/", ".");
        return {
          title: modelNamespacedApiIdentifier,
          itemId: `${apiReferenceBase}/schema/${modelUrlIdentifier}`,
          nodeName: "H1" as const as PageLevelHeading["nodeName"],
        };
      }),
  ];

  if (!hasGlobalActions && !hasGlobalViews) return { models };

  const globals: any = [
    {
      title: <Divider />,
      itemId: `${apiReferenceBase}/global-actions/divider`,
      disabled: true,
      onlyHeader: GadgetOnlyHeader.Header,
    },
  ];

  if (hasGlobalActions)
    globals.push({
      title: "Global actions",
      itemId: `${apiReferenceBase}/schema/global-actions`,
      nodeName: "H1" as const,
    });

  if (hasGlobalViews)
    globals.push({
      title: "Global views",
      itemId: `${apiReferenceBase}/schema/global-views`,
      nodeName: "H1" as const,
    });

  return {
    models,
    globals,
  };
};
